import { ApolloQueryResult, useApolloClient } from '@apollo/client';
import { first, isNil } from 'lodash';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Queries } from '@shared/api';
import { findMetric } from '@shared/scenes/billing/metricsCounter/container';
import { API } from '@shared/types/api';
import { useCurrentBusinessId, useStableValue } from '@shared/util/hooks';

export interface Billing {
  subscriptions?: NonNullable<
    NonNullable<API.GetBillingQuery['billing']>['customer']['subscriptions']
  >[number][];
  status?: API.BillingSubscriptionStatus;
  isPaidPlan?: boolean;
  messages?: number;
  messageLimit?: number;
  cards?: number;
  cardLimit?: number;
  storage?: number;
  storageLimit?: number;
  numUsers?: number;
}

export interface BillingContextValue {
  billing: Billing;
  loading: boolean;
  refetch: () => Promise<void>;
}

/**
 * Source: https://github.com/coast-app/core-api/blob/c5a6175e1afc9d637738ce129afe65bb048efada/packages/core-api/src/modules/billing/stripe/StripeClient.ts#L231
 */
const ACTIVE_SUBSCRIPTION_STATUSES: API.BillingSubscriptionStatus[] = [
  'active',
  'trialing',
  'past_due',
  'incomplete',
];

const defaultBilling: Billing = {};

const BillingContext = createContext<BillingContextValue>({
  billing: defaultBilling,
  loading: false,
  refetch: () => Promise.resolve(),
});

interface Props {
  children: ReactNode;
}

export function MBBillingProvider({ children }: Props) {
  const businessId = useCurrentBusinessId();
  const [billing, setBilling] = useStableValue<Billing>(defaultBilling);
  const [loading, setLoading] = useState(false);
  const apolloClient = useApolloClient();

  const refetch = useCallback(async () => {
    if (isNil(businessId)) {
      return;
    }
    setLoading(true);

    const usageQuery = apolloClient.query<API.ListUsageMetricsQuery>({
      query: Queries.billing.usageMetrics,
      fetchPolicy: 'network-only',
    });
    const billingQuery = apolloClient.query<API.GetBillingQuery>({
      query: Queries.billing.get,
      fetchPolicy: 'network-only',
    });
    const userCountQuery = apolloClient.query<
      API.GetUserCountQuery,
      API.GetUserCountQueryVariables
    >({
      query: Queries.billing.getUserCount,
      variables: { businessId },
      fetchPolicy: 'network-only',
    });

    try {
      const [usageData, billingData, userCountData] = await Promise.all([
        usageQuery,
        billingQuery,
        userCountQuery,
      ]);

      if (!usageData || !billingData || !userCountData) {
        setLoading(false);
        return;
      }

      const updatedBilling = getBillingState(usageData, billingData, userCountData);
      setBilling(updatedBilling);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [apolloClient, businessId, setBilling]);

  useEffect(() => {
    // a slight delay to avoid an 'invariant violation' which causes Apollo to
    // cancel the query when switching orgs
    const timer = setTimeout(() => refetch());
    return () => clearTimeout(timer);
  }, [refetch]);

  const value = { billing, loading, refetch };

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}

export function useBillingContext() {
  return useContext(BillingContext);
}

function getBillingState(
  usageData: ApolloQueryResult<API.ListUsageMetricsQuery>,
  billingData: ApolloQueryResult<API.GetBillingQuery>,
  userCountData: ApolloQueryResult<API.GetUserCountQuery>,
): Billing {
  const subscriptions = billingData.data.billing?.customer.subscriptions ?? undefined;
  if (isNil(subscriptions)) {
    return defaultBilling;
  }

  const subscription = first(subscriptions);
  const isPaidPlan = subscriptions.some(
    item => ACTIVE_SUBSCRIPTION_STATUSES.includes(item.status) && item.isPaidPlan,
  );

  const messages = findMetric('MESSAGES', usageData.data);
  const storage = findMetric('STORAGE', usageData.data);
  const cards = findMetric('CARDS', usageData.data);

  const messageLimit = subscription?.limits?.find(limit => limit.type === 'MESSAGES')?.value;
  const storageLimit = subscription?.limits?.find(limit => limit.type === 'STORAGE')?.value;
  const cardLimit = subscription?.limits?.find(limit => limit.type === 'CARDS')?.value;

  return {
    subscriptions,
    status: subscription?.status,
    isPaidPlan,
    messages,
    messageLimit,
    cards,
    cardLimit,
    storage,
    storageLimit,
    numUsers: userCountData?.data?.me?.business?.userCount ?? 0,
  };
}
